import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { UseAudioPlayerReturn } from './use-audio-player';

import { ScrollController } from '@/lib/teleprompter/scroll-controller';
import { TimingEngine } from '@/lib/teleprompter/timing-engine';

export interface UseAutoScrollReturn {
  currentSectionIndex: number;
  setCurrentSectionIndex: (index: number) => void;
}

export function useAutoScroll(
  containerRef: RefObject<HTMLDivElement | null>,
  audioPlayer: UseAudioPlayerReturn,
  sectionDurations: number[],
  settingsKey?: number,
  scrollContainerRef?: RefObject<HTMLElement | null>,
): UseAutoScrollReturn {
  const [manualSectionIndex, setManualSectionIndex] = useState(0);
  const controllerRef = useRef<ScrollController | null>(null);
  const timingEngineRef = useRef<TimingEngine | null>(null);
  const audioPlayerRef = useRef(audioPlayer);
  audioPlayerRef.current = audioPlayer;

  // Measure DOM and create TimingEngine
  useEffect(() => {
    const container = containerRef.current;
    const scrollContainer = scrollContainerRef?.current;
    if (!container || sectionDurations.length === 0) {
      timingEngineRef.current = null;
      return;
    }

    const elements = container.querySelectorAll('[data-section]');
    const tops: number[] = [];
    const heights: number[] = [];

    elements.forEach((el) => {
      // Use offsetTop chain instead of getBoundingClientRect so
      // positions are correct even when CSS transforms are applied
      tops.push(getOffsetTopRelativeTo(el as HTMLElement, scrollContainer ?? null));
      heights.push((el as HTMLElement).clientHeight);
    });

    if (tops.length !== sectionDurations.length) {
      timingEngineRef.current = null;
      return;
    }

    timingEngineRef.current = new TimingEngine(sectionDurations, tops, heights);
  }, [containerRef, sectionDurations, settingsKey, scrollContainerRef]);

  // Create/start/stop ScrollController based on isPlaying
  useEffect(() => {
    if (audioPlayer.isPlaying && timingEngineRef.current) {
      const scrollContainer = scrollContainerRef?.current;
      const controller = new ScrollController(
        timingEngineRef.current,
        () => audioPlayerRef.current.currentTimeMs,
        scrollContainer
          ? (y: number) => scrollContainer.scrollTo(0, y)
          : (y: number) => window.scrollTo(0, y),
        scrollContainer ? () => scrollContainer.clientHeight / 2 : () => window.innerHeight / 2,
      );
      controllerRef.current = controller;
      controller.start();

      return () => {
        controller.destroy();
        controllerRef.current = null;
      };
    } else {
      controllerRef.current?.destroy();
      controllerRef.current = null;
    }
  }, [audioPlayer.isPlaying, scrollContainerRef]);

  // Sync section index from audio when playing
  const currentSectionIndex = audioPlayer.isPlaying
    ? audioPlayer.currentSectionIndex
    : manualSectionIndex;

  const setCurrentSectionIndex = useCallback((index: number) => {
    setManualSectionIndex(index);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, []);

  return {
    currentSectionIndex,
    setCurrentSectionIndex,
  };
}

/**
 * Calculate an element's top offset relative to a container by walking the
 * offsetParent chain. Uses layout positions (offsetTop) rather than
 * getBoundingClientRect so results are unaffected by CSS transforms.
 */
function getOffsetTopRelativeTo(el: HTMLElement, container: HTMLElement | null): number {
  let top = 0;
  let current: HTMLElement | null = el;
  while (current && current !== container) {
    top += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return top;
}
